import {React, observer, Popover2, useLocalStore, useAsObservableSource, action, observable, utils} from '@api'
import {ContextMenuItem} from '@modules/contextMenus'
import {FrameShape, FrameShapeStore, SelectedShapesFloatingToolbarStore, shapeIcons, TDShape, TldrawApp, useTldrawApp, useWhiteboardStore, WhiteboardPage} from '@modules/whiteboard'
import {Button, EditableText, Menu, MenuDivider, MenuItem} from '@blueprintjs/core'
import {NumberBadge, PrimaryIconButton} from 'centric-ui-components'
import {computed} from 'mobx'
import * as css from './PageMenu.css'

export class WhiteboardPageMenuStore {
  constructor(public tldraw: TldrawApp) {}

  @observable editNameValue: { [pageId: string]: string } = {}

  @computed get isEditingNames() { return !_.isEmpty(this.editNameValue)}
}

export const PageMenu = observer(function PageMenu() {
  const {currentPageName, page, pagesInOrder, pageMenu: {isEditingNames}} = useTldrawApp()

  return (
    <Popover2
      interactionKind='hover'
      position='bottom-left'
      isOpen={isEditingNames ? true : undefined}
      content={<PageMenuContent/>}>
      <Button minimal text={<div className={css.currentPage}><NumberBadge value={pagesInOrder.indexOf(page) + 1}/>{currentPageName || 'Page'}</div>} rightIcon={'caret-down'}/>
    </Popover2>)
})

export const PageMenuContent = observer(function PageMenuContent() {
  const {createNewPage, pagesInOrder} = useWhiteboardStore()

  return (
    <Menu>
      {pagesInOrder.map((p, i) => <PageMenuItem page={p} key={p.id} index={i}/>)}
      <MenuDivider/>
      <MenuItem icon={'page-layout'} shouldDismissPopover={false} text={'New Page'} onClick={createNewPage}/>
    </Menu>
  )
})

export class PageMenuItemStore {
  constructor(public props: { tldraw: TldrawApp, index: number, page: WhiteboardPage }) {}

  get tldraw() { return this.props.tldraw}

  get page() { return this.props.page}

  get pageWrapper() { return this.tldraw.pageWrapperById[this.page.id]}

  @computed get framesInOrder() {
    return this.pageWrapper.shapesInOrder.filter(s => s.type === 'frame')
  }

  @action onSelectPage = () => {
    const {tldraw: {selectPage}, page} = this
    selectPage(page)
  }

  @computed get editValue() { return this.tldraw.pageMenu.editNameValue[this.page.id] }

  get pageMenu() { return this.tldraw.pageMenu}

  @action onClickEditButton = (e?: React.MouseEvent) => {
    e?.stopPropagation()

    const {pageMenu, page: {id}, editValue} = this
    if (editValue === undefined) {
      pageMenu.editNameValue[id] = this.page.name
    } else {
      delete pageMenu.editNameValue[id]
    }
  }

  @action onChangePageName = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.pageMenu.editNameValue[this.page.id] = e.currentTarget.value
  }

  @action onChangePageNameEditable = (v: string) => {
    this.pageMenu.editNameValue[this.page.id] = v
  }

  @action onCancelPageNameChange = () => {
    if (this.isEditing) {
      this.onClickEditButton()
    } else {
      this.page.name = null
    }
  }

  @action onConfirmPageNameChange = () => {
    if (this.editValue) {
      this.page.name = this.editValue
    }

    this.onClickEditButton()
  }

  @computed get label() {
    const {page: {name}, props: {index}} = this
    return name ?? `Page ${index + 1}`
  }

  @computed get isEditing() { return this.editValue !== undefined}

  @action onDeletePage = () => { this.tldraw.deletePage(this.page.id)}

  @action onSelectShape = (shape: TDShape) => (e) => {
    this.pageWrapper.selectShape(shape)
  }
}

export const PageMenuItemStoreContext = React.createContext<PageMenuItemStore>(null)
export const usePageMenuItemStore = () => React.useContext(PageMenuItemStoreContext)

export const PageMenuItem = observer<{ page: WhiteboardPage, index: number }>(function PageMenuItem({page, index}) {
  const tldraw = useWhiteboardStore(),
    props = useAsObservableSource({page, tldraw, index}),
    store = useLocalStore(() => new PageMenuItemStore(props)),
    {
      page: {id}, pageWrapper: {shapesInOrder}, isEditing, onSelectShape, label, onSelectPage, onClickEditButton, tldraw: {currentPageId},
      editValue, framesInOrder, onChangePageName, onChangePageNameEditable, onCancelPageNameChange, onDeletePage, onConfirmPageNameChange
    } = store

  return (<PageMenuItemStoreContext.Provider value={store}>
    <MenuItem
      className={css.pageMenuItem}
      shouldDismissPopover={false}
      data-editing={isEditing}
      icon={<NumberBadge value={index + 1} selected={id === currentPageId}/>}
      text={isEditing
            ? <div className={css.inputWrapper} onClick={utils.eatMouseEvent}>
              {true ? <input className={css.editPageName}
                             placeholder={`Page ${index + 1}`} value={editValue} onChange={onChangePageName}/>
                    : <EditableText className={css.editPageName} placeholder={label} value={editValue}
                                    onCancel={onCancelPageNameChange}
                                    onConfirm={onConfirmPageNameChange}
                                    onChange={onChangePageNameEditable}/>}
            </div>
            : label}
      onClick={onSelectPage}
      labelElement={
        <div className={css.buttonBar} onClick={utils.eatMouseEvent}>
          <PrimaryIconButton icon='edit' onClick={onClickEditButton}/>
          {(isEditing || page.name != null) && <PrimaryIconButton icon='cross' onClick={onCancelPageNameChange}/>}
          {tldraw.pagesInOrder.length > 1 && <PrimaryIconButton icon='trash' onClick={onDeletePage}/>}
        </div>}
      children={_.isEmpty(framesInOrder) ? undefined : shapesInOrder.filter(s => s.type === 'frame').map(
        (s: FrameShape, i) => (<FrameMenuItem frame={s} key={s.id} index={i}/>))}/>
  </PageMenuItemStoreContext.Provider>)
})

export const FrameMenuItem = observer<{ frame: FrameShape, index: number }>(function FrameMenuItem({frame, index}) {
  const {shapeComponentStores, shapeUtil: {getTitle}} = useTldrawApp(),
    frameStore = shapeComponentStores[frame.id] as FrameShapeStore,
    {onSelectShape} = usePageMenuItemStore(),
    {title} = frame

  return (<ContextMenuItem
    icon={shapeIcons.frame}
    text={title ?? `Frame ${index + 1}`}
    active={frameStore?.props?.isSelected}
    onClick={onSelectShape(frame)}
    children={
      _.isEmpty(frameStore?.shapesContainedByInOrder)
      ? undefined
      : frameStore.shapesContainedByInOrder.map(
        (child, i) => (
          <ContextMenuItem
            className={css.frameContentItem}
            key={child.id}
            icon={shapeIcons[child.type]}
            text={getTitle(child)}
            onClick={onSelectShape(child)}/>
        ))}
  />)
})


// WEBPACK FOOTER //
// ./src/modules/whiteboard/components/TopPanel/PageMenu/PageMenu.tsx